import React, { useEffect, useState } from 'react';
import {
  ClientMenuLink,
  ClientMenuExternalLink,
  ClientSuperMenuItem,
} from '../types';
import { checkIsOpenedMenu, checkIsRootMenu } from './utils';

interface Props {
  menuLink: ClientMenuLink | ClientMenuExternalLink;
  index: number;
  menuRoot: string | null;
}

const MenuLink = ({ menuLink, index, menuRoot }: Props) => {
  const [clientExpanded, setClientExpanded] = useState(menuLink.serverExpanded);
  const [isLoading, setIsLoading] = useState(false);
  const [clientActive, setClientActive] = useState(false);
  const [indicatorClass, setIndicatorClass] = useState('menu-indicator');
  let icon: JSX.Element | undefined;

  if (menuLink.children && !clientExpanded && !isLoading) {
    icon = <div className="right-caret"></div>;
  } else if (menuLink.children && clientExpanded && !isLoading) {
    icon = <div className="down-caret"></div>;
  } else if (
    !menuLink.url?.includes('#') &&
    isLoading &&
    menuLink.type !== 'externalMenuLink'
  ) {
    icon = <div className="loading-spinner"></div>;
  } else {
    icon = undefined;
  }
  const getMenuClass = () => {
    let menuClass = '';
    if (menuLink.children) {
      menuClass += 'has-menu ';
    }
    if (menuLink.active) {
      menuClass += 'is-active ';
    }
    if (clientActive) {
      menuClass += 'is-active-anchor ';
    }
    if (menuLink.children && clientExpanded) {
      menuClass += 'is-expanded';
    }
    return menuClass;
  };

  const handleClick = (event) => {
    if (menuLink.active || getMenuClass().split(' ').includes('is-expanded')) {
      setClientExpanded(!clientExpanded);
      event.preventDefault();
      return;
    }
    setIsLoading(true);
  };

  const setActive = () => {
    const pathname = '/' + window.location.href.split('/').slice(3).join('/');
    const isOpenedMenu = checkIsOpenedMenu(menuLink, pathname);
    let isRootMenu;
    if (menuRoot) {
      isRootMenu = checkIsRootMenu(menuLink, menuRoot);
      setIndicatorClass(
        isRootMenu
          ? 'menu-root-indicator'
          : isOpenedMenu
            ? 'menu-current-indicator'
            : 'menu-indicator',
      );
    } else {
      // Serve single leaf
      isRootMenu =
        menuLink.parent && (menuLink as any).parent.type === 'docLeafMenu';
      setIndicatorClass(
        isOpenedMenu
          ? isRootMenu
            ? 'menu-root-indicator'
            : 'menu-current-indicator'
          : 'menu-indicator',
      );
    }
    const active =
      typeof window !== 'undefined' &&
      window.location.href.endsWith(menuLink.url);
    if (menuLink.url?.includes('#')) {
      setClientActive(active);
    }
  };

  useEffect(() => {
    setActive();
    window.addEventListener('popstate', setActive);
    return () => {
      window.removeEventListener('popstate', setActive);
    };
  }, []);

  return (
    <li key={`li-${index}`}>
      <a
        target={menuLink.type === 'externalMenuLink' ? '_blank' : ''}
        rel="noopener noreferrer"
        href={menuLink.path}
        data-test-id="menu-link"
        key={`a-${index}`}
        className={getMenuClass()}
        onClick={handleClick}
      >
        {menuLink.text}
        {icon}
        <div key={`div-${index}`} className={indicatorClass}></div>
      </a>
      {/* This is for the very rare occassion where we have a link with children,
      this is different than a MenuGroup. */}
      {menuLink.children && (
        <ul key={`ul-${index}`}>
          {clientExpanded &&
            (menuLink.children as ClientSuperMenuItem[]).map((child, i) => {
              return (
                <MenuLink
                  menuLink={child as ClientMenuLink}
                  index={i}
                  key={`menulink-${i}`}
                  menuRoot={menuRoot}
                />
              );
            })}
        </ul>
      )}
    </li>
  );
};

export default MenuLink;
